import React, { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import DataTable from '@frontend/app/refresh-components/DataTable';
import { topSocialAccountsColumns } from './top-social-accounts-columns';
import type { SocialAccountData } from '../social-accounts-table/social-accounts-table-types';
import { sharedRenderRowSocialAccounts } from './shared-render-row-social-accounts';

interface TopSocialAccountsTableProps {
  data: SocialAccountData[];
  isLoading?: boolean;
  isRefreshing?: boolean;
}

export const TopSocialAccountsTable: React.FC<TopSocialAccountsTableProps> = ({
  data,
  isLoading = false,
  isRefreshing = false,
}) => {
  const columns: ColumnDef<SocialAccountData>[] = useMemo(() => {
    return topSocialAccountsColumns.map((col) => {
      const accessorKey = (col as { accessorKey?: keyof SocialAccountData }).accessorKey;
      const id = (col as { id?: string; accessorKey?: keyof SocialAccountData }).id ?? accessorKey;
      const rowType = (col as { rowType?: string; accessorKey?: keyof SocialAccountData }).rowType ?? accessorKey;
      return {
        ...col,
        id,
        // Remove any tooltip property from meta to prevent DataTable from rendering tooltips
        meta: {
          ...col.meta,
          tooltip: undefined, // Explicitly remove tooltip to prevent overlay issues
        },
        cell: ({ row }) => {
          if (isRefreshing) {
            return <div className="h-4 rounded bg-gray-100 animate-pulse w-full" />;
          }
          return sharedRenderRowSocialAccounts(rowType, row, col, true);
        },
      };
    });
  }, [isRefreshing]);

  if (isLoading) {
    // Skeleton loader: 7 columns, 5 rows
    return (
      <div
        className="w-full h-full flex flex-col bg-white rounded-lg border border-gray-200"
        role="status"
        aria-busy="true"
        aria-label="Loading social accounts table"
      >
        {/* Skeleton Table Header */}
        <div className="flex border-b border-gray-200 px-6 py-3">
          {[...Array(7)].map((_, idx) => (
            <div
              key={idx}
              className={['h-4 rounded bg-gray-200 animate-pulse flex-1', idx !== 6 ? 'mr-4' : ''].join(' ')}
            />
          ))}
        </div>
        {/* Skeleton Table Rows */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {[...Array(5)].map((_, rowIdx) => (
            <div
              key={rowIdx}
              className={[
                'flex items-center border-b border-gray-100 px-6 py-4 last:border-b-0',
                rowIdx === 4 ? 'last:rounded-b-lg' : '',
              ].join(' ')}
            >
              {[...Array(7)].map((_, colIdx) => (
                <div
                  key={colIdx}
                  className={['h-4 rounded bg-gray-100 animate-pulse flex-1', colIdx !== 6 ? 'mr-4' : ''].join(' ')}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return <DataTable columns={columns} data={data} bordered tableContentClassName="border-b-0" />;
};
